import React, { useState, useRef, useEffect } from 'react';
import { SearchIcon, ChevronDownIcon } from '@heroicons/react/solid';
import { classNames } from '../../../../helpers/classNames';

const SearchableColumnSelect = ({
    columns = [],
    value,
    onChange,
    placeholder = "Select a column...",
    excludeTypes = [],
    className = "",
    label = "",
    disabled = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);

    // Filter columns based on search query and excluded types
    const filteredColumns = columns.filter(column => {
        if (excludeTypes.includes(column.col_type)) return false;

        const searchLower = searchQuery.toLowerCase();
        return (
            column.title.toLowerCase().includes(searchLower) ||
            column.col_id.toLowerCase().includes(searchLower) ||
            column.col_type.toLowerCase().includes(searchLower)
        );
    });

    // Find selected column
    const selectedColumn = columns.find(col => col.col_id === value);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchQuery('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Focus search input when dropdown opens
    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            setTimeout(() => searchInputRef.current.focus(), 100);
        }
    }, [isOpen]);

    const handleSelect = (column) => {
        onChange(column.col_id);
        setIsOpen(false);
        setSearchQuery('');
    };

    const handleToggle = () => {
        if (!disabled) {
            setIsOpen(!isOpen);
            setSearchQuery('');
        }
    };

    return (
        <div className="relative" ref={dropdownRef}>
            {label && (
                <label className="block text-xs font-medium text-gray-700 mb-1">
                    {label}
                </label>
            )}

            {/* Selected value display */}
            <button
                type="button"
                onClick={handleToggle}
                disabled={disabled}
                className={classNames(
                    "w-full text-left px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm bg-white",
                    disabled ? "bg-gray-50 text-gray-500 cursor-not-allowed" : "hover:bg-gray-50",
                    className
                )}
            >
                <div className="flex items-center justify-between">
                    <span className={classNames(
                        "truncate",
                        selectedColumn ? "text-gray-900" : "text-gray-500"
                    )}>
                        {selectedColumn ? selectedColumn.title : placeholder}
                    </span>
                    <ChevronDownIcon className={classNames(
                        "h-4 w-4 text-gray-400 transition-transform duration-200",
                        isOpen ? "transform rotate-180" : ""
                    )} />
                </div>
            </button>

            {/* Dropdown */}
            {isOpen && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
                    {/* Search input */}
                    <div className="p-2 border-b border-gray-200">
                        <div className="relative">
                            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                                ref={searchInputRef}
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search columns..."
                                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>
                    </div>

                    {/* Options list */}
                    <div className="max-h-40 overflow-y-auto">
                        {filteredColumns.length === 0 ? (
                            <div className="px-3 py-4 text-sm text-gray-500 text-center">
                                {searchQuery ? 'No columns match your search' : 'No columns available'}
                            </div>
                        ) : (
                            filteredColumns.map((column) => (
                                <button
                                    key={column.col_id}
                                    type="button"
                                    onClick={() => handleSelect(column)}
                                    className={classNames(
                                        "w-full text-left px-3 py-2 text-sm hover:bg-blue-50 focus:bg-blue-50 focus:outline-none",
                                        value === column.col_id ? "bg-blue-100 text-blue-900" : "text-gray-900"
                                    )}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium truncate">{column.title}</p>
                                        </div>
                                        {value === column.col_id && (
                                            <div className="ml-2 text-blue-600">
                                                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                        )}
                                    </div>
                                </button>
                            ))
                        )}
                    </div>

                    {/* Show search results count */}
                    {searchQuery && (
                        <div className="px-3 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-600">
                            {filteredColumns.length} of {columns.filter(col => !excludeTypes.includes(col.col_type)).length} columns
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SearchableColumnSelect; 